import type {ReactElement} from 'react';
import type {UserMailingList} from '@Components/email-marketing-campaign-performance/email-marketing-campaign-performance.types';
import {ClickableDiv} from '@Components/clickable-div';
import styles from '@Components/email-maker-show-mailing-lists-panel/email-maker-show-mailing-lists-panel.module.scss';
import {Icon} from '@Components/icon-v2';
import {Checkbox} from '@Components/checkbox';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import SpamMailingListControls from '@Components/email-maker-show-mailing-lists-panel/spam-mailing-list-controls/spam-mailing-list-controls';
import {EmailMakerShowMailingListsPanelSize} from '@Components/email-maker-show-mailing-lists-panel';
import MailingListEditIcon from '@Components/email-maker-show-mailing-lists-panel/mailing-list-edit-icon/mailing-list-edit-icon';
import {escapeHTML} from '@Utils/string.util';

export interface SelectableMailingListProps {
  mailingList: UserMailingList;
  onCheckToggle: (mailingList: UserMailingList) => void;
  isMailingListSelected: boolean;
  isMailingListDisabled: boolean;
  onSpamInfoClick: VoidFunction;
  onClickDelete: (mailingList: UserMailingList) => void;
  onClickEdit: (mailingList: UserMailingList) => void;
  size: EmailMakerShowMailingListsPanelSize;
}

export function SelectableMailingList({
  mailingList,
  onCheckToggle,
  isMailingListSelected,
  isMailingListDisabled,
  onSpamInfoClick,
  onClickDelete,
  onClickEdit,
  size,
}: SelectableMailingListProps): ReactElement<any> {
  const getWarningIcon = (): ReactElement<any> => {
    return <Icon className={`color-danger ${styles.dangerIcon}`} icon={'icon-exclamation-triangle'}></Icon>;
  };

  const getMailingListSelector = (mailingList: UserMailingList): ReactElement<any> => {
    return mailingList.isSpam ? getWarningIcon() : getCheckboxIfApplicable();
  };

  const getCheckboxStyles = (): string => {
    if (isMailingListSelected) {
      return styles.checked;
    }

    return styles.unchecked;
  };

  const getCheckboxIfApplicable = (): ReactElement<any> => {
    return <Checkbox className={`${styles.checkbox} ${getCheckboxStyles()}`} size={IconSize.SIZE_ICON_16} isChecked={isMailingListSelected} />;
  };

  const getWrapperStyles = (mailingList: UserMailingList): string => {
    if (mailingList.isSpam) {
      return styles.danger;
    }

    if (isMailingListSelected) {
      return styles.selected;
    }

    if (isMailingListDisabled) {
      return styles.disabled;
    }

    return '';
  };

  const getSmartMailingListIconIfNeeded = (mailingList: UserMailingList): ReactElement<any> | null => {
    if (mailingList.isSmart) {
      return (
        <Icon
          className={`spacing-m-r-1 ${isMailingListSelected ? styles.selected : null} ${isMailingListDisabled ? 'content-disabled' : ''}`}
          icon={'icon-auto-mailing-list'}
          size={IconSize.SIZE_ICON_20}
        />
      );
    }

    return null;
  };

  const getClassesForMailingListDetails = (): string => {
    if (isMailingListSelected) {
      return styles.selected;
    }

    if (isMailingListDisabled) {
      return `content-disabled`;
    }

    return '';
  };

  const getNumberOfContactsInformation = (): ReactElement<any> => {
    let text = window.i18next.t('pmwjs_contacts');

    if (mailingList.mailingListContacts && mailingList.mailingListContacts.length === 1) {
      text = window.i18next.t('pmwjs_contact');
    }

    return <Text size={TextSize.XSMALL} className={`${styles.contactText} ${getClassesForMailingListDetails()}`} val={`${mailingList.mailingListContacts?.length ?? 0} ${text}`} />;
  };

  const getStylesBySize = (): string => {
    if (size === EmailMakerShowMailingListsPanelSize.MEDIUM) {
      return styles.medium;
    }

    if (size === EmailMakerShowMailingListsPanelSize.SMALL) {
      return styles.small;
    }

    return '';
  };

  const getMarkedSpamDetails = (mailingList: UserMailingList): ReactElement<any> => {
    return (
      <div className={styles.warningWrapper}>
        <div className={`${styles.markedAsSpamTextWrapper} ${getStylesBySize()}`}>
          <Text size={TextSize.XXSMALL} val={window.i18next.t('pmwjs_marked_as_spam')} bold className={'content-body-white'} />
        </div>

        <SpamMailingListControls
          mailingList={mailingList}
          onClickDelete={() => {
            onClickDelete(mailingList);
          }}
          onClickSpamInfo={onSpamInfoClick}
          iconWrapperClass={styles.iconWrapper}
        />
      </div>
    );
  };

  const getSpamMailingListDetails = (mailingList: UserMailingList): ReactElement<any> | null => {
    return mailingList.isSpam ? getMarkedSpamDetails(mailingList) : null;
  };

  const getEditIconIfApplicable = (mailingList: UserMailingList): ReactElement<any> | null => {
    const isMailingListCurrentlySelected = isMailingListSelected;
    return (
      <MailingListEditIcon
        mailingList={mailingList}
        isMailingListSelected={isMailingListCurrentlySelected}
        onEditMailingList={onClickEdit}
        isMailingListDisabled={isMailingListDisabled}
        iconClasses={`${styles.editIcon} ${isMailingListCurrentlySelected ? styles.selected : ''}`}
        containerClasses={styles.editIconContainer}
      />
    );
  };

  return (
    <ClickableDiv
      onClick={() => {
        onCheckToggle(mailingList);
      }}
      key={`id-${mailingList.id}`}
      className={`${styles.mailingListContainer} ${getWrapperStyles(mailingList)}`}
    >
      {getMailingListSelector(mailingList)}
      {getSmartMailingListIconIfNeeded(mailingList)}
      <Text className={getClassesForMailingListDetails()} bold size={TextSize.XSMALL} val={escapeHTML(mailingList.name)} />
      <div className={`${styles.separator} ${isMailingListDisabled ? styles.disabled : ''} ${getClassesForMailingListDetails()}`} />
      {getNumberOfContactsInformation()}
      {getSpamMailingListDetails(mailingList)}
      {getEditIconIfApplicable(mailingList)}
    </ClickableDiv>
  );
}
